/*
 * Copyright (C) 2026 Bohemian Miser <https://substack.com/@bohemianmiser>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import type { RecipeGraph, LayoutGraph, VisualNode, VisualEdge, VisualLane, RecipeNode } from './types';
import dagre from 'dagre';
import { buildTimelineLayout, TL } from './timeline-layout';

// Only keeping Lanes, Smart, Smart LR, Timeline
export type LayoutMode = 'swimlanes' | 'dagre' | 'dagre-lr' | 'timeline';

const CONSTANTS = {
  standard: {
    LANE_WIDTH: 220,
    NODE_WIDTH: 200,
    PADDING_TOP: 40,
    PADDING_LEFT: 30,
    GAP_Y: 20,
    INGREDIENT_HEIGHT: 50,
    ACTION_HEIGHT: 100
  },
  compact: { // Used as internal fallback for swimlanes compact mode if needed, or remove?
    LANE_WIDTH: 150,
    NODE_WIDTH: 140,
    PADDING_TOP: 20,
    PADDING_LEFT: 5,
    GAP_Y: 15,
    INGREDIENT_HEIGHT: 40,
    ACTION_HEIGHT: 80
  },
  dagre: {
    NODE_WIDTH: 140,
    NODE_HEIGHT_ING: 80,
    NODE_HEIGHT_ACT: 120,
    PADDING: 20
  }
};

const LANE_COLORS = {
  prep: '#EFF6FF', // Blue-50
  cook: '#FFF7ED', // Orange-50
  serve: '#F0FDF4', // Green-50
  default: '#FAFAFA'
};

// ── Timeline adapter ─────────────────────────────────────────────────────────

// Both node types are 40×40 circles (TL.NODE_R = 20)
export const TIMELINE_NODE_SIZE = TL.NODE_R * 2;   // 40px

function calculateTimelineLayout(graph: RecipeGraph): LayoutGraph {
  const tl = buildTimelineLayout(graph);

  const laneLineColor = new Map(tl.lanes.map(l => [l.laneId, l.lineColor]));

  const s = TIMELINE_NODE_SIZE;
  const nodes: VisualNode[] = tl.nodes.map(n => {
    const lineColor = laneLineColor.get(n.laneId) ?? '#D4D4D8';
    return {
      id: n.id,
      type: n.data.type,
      x: n.cx - s / 2,
      y: n.cy - s / 2,
      width: s,
      height: s,
      data: n.data,
      lineColor,
    };
  });

  // Edges: path is ignored by TimelineEdge (computed from node positions), but
  // lineColor and kind are used by the custom edge renderer.
  const tlNodeMap = new Map(tl.nodes.map(n => [n.id, n]));
  const edges: VisualEdge[] = tl.edges.map(e => {
    const src = tlNodeMap.get(e.sourceId);
    const tgt = tlNodeMap.get(e.targetId);
    const lineColor =
      e.kind === 'spur'
        ? (laneLineColor.get(tgt?.laneId ?? '') ?? '#D4D4D8')
        : (laneLineColor.get(src?.laneId ?? '') ?? '#D4D4D8');
    return {
      id: e.id,
      sourceId: e.sourceId,
      targetId: e.targetId,
      path: '',
      lineColor,
      kind: e.kind,
    };
  });

  // Lane bands – horizontal strips
  const lanes: VisualLane[] = tl.lanes.map(l => ({
    id: l.laneId,
    label: l.label,
    x: 0,
    y: l.yMin,
    width: tl.totalWidth,
    height: l.yMax - l.yMin,
    color: l.color,
  }));

  return {
    nodes, edges, lanes,
    width: tl.totalWidth,
    height: tl.totalHeight,
    timelineData: {
      pixelsPerMin:  tl.pixelsPerMin,
      totalMinutes:  tl.totalMinutes,
      actionZoneY:   tl.actionZoneY,
      totalHeight:   tl.totalHeight,
      rulerHeight:   TL.RULER_H,
      laneLabelWidth: TL.LANE_LABEL_W,
      gridInterval:  TL.GRID_INTERVAL,
    },
  };
}

// ── Public entry point ────────────────────────────────────────────────────────

export const calculateLayout = (graph: RecipeGraph, mode: LayoutMode = 'dagre', spacing: number = 1, preservePositions: boolean = false): LayoutGraph => {
  // Timeline needs its full structure (lanes, edge kinds, timelineData) even when
  // restoring saved positions — run the algo then override x/y from saved values.
  if (preservePositions && mode === 'timeline' && graph.nodes.some(n => n.x !== undefined)) {
    const layout = calculateTimelineLayout(graph);
    const savedPos = new Map(
      graph.nodes.filter(n => n.x !== undefined).map(n => [n.id, { x: n.x!, y: n.y! }])
    );
    return {
      ...layout,
      nodes: layout.nodes.map(n => {
        const pos = savedPos.get(n.id);
        return pos ? { ...n, x: pos.x, y: pos.y } : n;
      }),
    };
  }

  // If preserving positions (and at least one exists), bypass algo
  if (preservePositions && graph.nodes.some(n => n.x !== undefined)) {
      // Some nodes lack positions (e.g. just added by an AI patch). Run the full
      // layout algo to place them, then restore saved positions for the nodes that
      // already had them so the user's layout is not disturbed.
      const hasUnpositioned = graph.nodes.some(n => n.x === undefined);
      if (hasUnpositioned) {
          const rankDir = mode === 'dagre-lr' ? 'LR' : 'TB';
          const fullLayout = calculateDagreLayout(graph, spacing, rankDir);
          const savedPos = new Map(
              graph.nodes.filter(n => n.x !== undefined).map(n => [n.id, { x: n.x!, y: n.y! }])
          );
          fullLayout.nodes.forEach(n => {
              const saved = savedPos.get(n.id);
              if (saved) { n.x = saved.x; n.y = saved.y; }
          });
          return fullLayout;
      }

      const nodes: VisualNode[] = graph.nodes.map(n => ({
          id: n.id,
          type: n.type,
          x: n.x ?? 0,
          y: n.y ?? 0,
          width: 140,
          height: 100,
          depth: (n as any).depth, // Preserve depth if available
          data: n
      }));
      
      const edges: VisualEdge[] = [];
      const nodePosMap = new Map();
      nodes.forEach(n => nodePosMap.set(n.id, n));
      generateEdges(graph, nodes, nodePosMap, edges, 'vertical'); 

      // Reconstruct Lanes if in Swimlanes mode
      let visualLanes: VisualLane[] = [];
      if (mode === 'swimlanes' && graph.lanes && graph.lanes.length > 0) {
          const BaseC = CONSTANTS.standard;
          const C = {
              PADDING_LEFT: BaseC.PADDING_LEFT * spacing,
              LANE_WIDTH: BaseC.LANE_WIDTH * spacing,
              PADDING_TOP: BaseC.PADDING_TOP * spacing
          };

          // Calculate height based on content
          let maxY = 0;
          nodes.forEach(n => {
              const bottom = n.y + (n.height || 100);
              if (bottom > maxY) maxY = bottom;
          });
          const layoutHeight = Math.max(maxY + 50, 600) + C.PADDING_TOP;

          visualLanes = graph.lanes.map((lane, idx) => ({
              id: lane.id,
              label: lane.label,
              x: C.PADDING_LEFT + idx * C.LANE_WIDTH,
              y: 0,
              width: C.LANE_WIDTH,
              height: layoutHeight,
              color: LANE_COLORS[lane.type] || LANE_COLORS.default
          }));
      }

      return {
          nodes,
          edges,
          lanes: visualLanes, 
          width: 2000,
          height: 2000
      };
  }

  switch (mode) {
    case 'dagre':
      return calculateDagreLayout(graph, spacing, 'TB');
    case 'dagre-lr':
      return calculateDagreLayout(graph, spacing, 'LR');
    case 'timeline':
      return calculateTimelineLayout(graph);
    case 'swimlanes':
    default:
      // Default to standard swimlane if 'swimlanes' selected
      return calculateSwimlaneLayout(graph, 'standard', spacing);
  }
};

// --- DAGRE ALGORITHM ---
const calculateDagreLayout = (graph: RecipeGraph, spacing: number, rankDir: 'TB' | 'LR' = 'TB'): LayoutGraph => {
    const C = CONSTANTS.dagre;
    const g = new dagre.graphlib.Graph();
    console.log(`Calculating Dagre Layout with rankDir=${rankDir} and spacing=${spacing}, ${(0.7*spacing-0.5 +0.05)}`);
    g.setGraph({ 
        rankdir: rankDir, 
        nodesep: 100 * (0.7*spacing-0.5 +0.05), 
        ranksep: 200 * (0.7*spacing-0.5 +0.05)
    });
    g.setDefaultEdgeLabel(() => ({}));

    graph.nodes.forEach(node => {
        const height = node.type === 'ingredient' ? C.NODE_HEIGHT_ING : C.NODE_HEIGHT_ACT;
        const width = node.type === 'ingredient' ? C.NODE_WIDTH : 280;
        g.setNode(node.id, { width: width, height: height, data: node });
    });

    graph.nodes.forEach(node => {
        if (node.inputs) {
            node.inputs.forEach(inputId => {
                if (graph.nodes.find(n => n.id === inputId)) {
                   g.setEdge(inputId, node.id);
                }
            });
        }
    });

    dagre.layout(g);

    if (rankDir === 'LR') {
       // Flip Y coordinates to ensure Top-to-Bottom reading order logic if needed
       // Dagre LR naturally goes Left to Right.
       // Usually we don't need to flip Y for LR unless we want upside down?
       // Step 20 logic: "flip Y coordinates to ensure Top-to-Bottom reading order".
       // Actually Dagre LR puts 0,0 at top left.
       // If I flip Y, I might mirror it.
       // I'll keep the flip logic if it was requested to fix "upside down feel".
       // "Smart (LR) layout to be top-to-bottom" -> Wait, LR is Left-Right.
       // Maybe they meant the *content* order?
       // I'll trust previous fix logic.
       let maxY = 0;
       g.nodes().forEach(v => { const n = g.node(v); maxY = Math.max(maxY, n.y + n.height/2); });
       g.nodes().forEach(v => { const n = g.node(v); n.y = maxY - n.y; });
       g.edges().forEach(e => { g.edge(e).points.forEach((p: any) => p.y = maxY - p.y); });
    }

    const nodes: VisualNode[] = [];
    const edges: VisualEdge[] = [];

    g.nodes().forEach(v => {
        const node: any = g.node(v);
        // Dagre x,y is center. We use top-left.
        nodes.push({
            id: v,
            type: node.data.type,
            x: node.x - node.width / 2 + C.PADDING,
            y: node.y - node.height / 2 + C.PADDING,
            width: node.width,
            height: node.height,
            data: node.data
        });
    });

    g.edges().forEach(e => {
        const edge = g.edge(e);
        const pathPoints = edge.points.map((p: any) => `${p.x + C.PADDING},${p.y + C.PADDING}`);
        const d = `M ${pathPoints.join(' L ')}`;
        
        edges.push({
            id: `${e.v}->${e.w}`,
            sourceId: e.v,
            targetId: e.w,
            path: d
        });
    });

    // Calculate bounding box
    const layoutWidth = (g.graph().width || 800) + C.PADDING * 2;
    const layoutHeight = (g.graph().height || 600) + C.PADDING * 2;

    return {
        nodes,
        edges,
        lanes: [], // No lanes in pure Dagre
        width: layoutWidth,
        height: layoutHeight
    };
};


// --- SWIMLANE / COMPACT ALGORITHM ---
const calculateSwimlaneLayout = (graph: RecipeGraph, mode: 'standard' | 'compact', spacing: number): LayoutGraph => {
  const BaseC = CONSTANTS[mode] || CONSTANTS.standard;
  const C = {
      ...BaseC,
      PADDING_TOP: BaseC.PADDING_TOP * spacing,
      PADDING_LEFT: BaseC.PADDING_LEFT * spacing,
      GAP_Y: BaseC.GAP_Y * spacing*2,
      // Dampen Lane Width scaling so spacing affects edges (vertical) more than lanes (horizontal)
      LANE_WIDTH: BaseC.LANE_WIDTH * Math.pow(spacing, 0.5) 
  };
  const nodes: VisualNode[] = [];
  const edges: VisualEdge[] = [];
  
  if (!graph.lanes.length) return emptyGraph();

  const laneMap = new Map<string, number>();
  graph.lanes.forEach((lane, idx) => laneMap.set(lane.id, idx));

  const visualLanes: VisualLane[] = graph.lanes.map((lane, idx) => ({
    id: lane.id,
    label: lane.label,
    x: C.PADDING_LEFT + idx * C.LANE_WIDTH,
    y: 0,
    width: C.LANE_WIDTH,
    height: 0,
    color: LANE_COLORS[lane.type] || LANE_COLORS.default
  }));

  const { ranks, consumerMap } = analyzeGraph(graph);

  // Group Ingredients by Action for Clustering
  const actionIngredients = new Map<string, string[]>();
  const clusteredIngredients = new Set<string>();

  graph.nodes.forEach(node => {
      if (node.type === 'ingredient') {
          // Identify consumers within the same lane
          const consumers = consumerMap.get(node.id) || [];
          const localConsumers = consumers.map(id => graph.nodes.find(n => n.id === id)).filter(n => n && n.laneId === node.laneId);
          
          if (localConsumers.length === 1 && localConsumers[0]) {
              const actionId = localConsumers[0].id;
              if (!actionIngredients.has(actionId)) actionIngredients.set(actionId, []);
              actionIngredients.get(actionId)!.push(node.id);
              clusteredIngredients.add(node.id);
          }
      }
  });

  // Sort nodes
  const sortedNodes = [...graph.nodes].sort((a, b) => {
    const rA = ranks.get(a.id) || 0;
    const rB = ranks.get(b.id) || 0;
    if (rA !== rB) return rA - rB;
    if (a.type !== b.type) return a.type === 'ingredient' ? -1 : 1;
    const lA = laneMap.get(a.laneId) || 0;
    const lB = laneMap.get(b.laneId) || 0;
    if (lA !== lB) return lA - lB;
    return a.id.localeCompare(b.id);
  });

  const nodePosMap = new Map<string, { x: number, y: number, width: number, height: number }>();
  const laneYCursors = new Array(graph.lanes.length).fill(C.PADDING_TOP);

  sortedNodes.forEach(node => {
    // Skip if clustered ingredient (will be handled by action)
    if (clusteredIngredients.has(node.id)) return;

    const laneIdx = laneMap.get(node.laneId) || 0;
    let width = node.type === 'action' ? 280 : C.NODE_WIDTH;
    let height = node.type === 'ingredient' ? C.INGREDIENT_HEIGHT : C.ACTION_HEIGHT;
    
    // If Action, check for clustered ingredients to place FIRST
    if (node.type === 'action' && actionIngredients.has(node.id)) {
        const ingredients = actionIngredients.get(node.id)!;
        
        // Ensure dependencies of these ingredients are met?
        // Ingredients usually are roots (no inputs), so we assume safe.
        // If they had inputs, we might have an issue if those weren't placed.
        // But in recipe graphs, ingredients are inputs.
        
        const clusterY = laneYCursors[laneIdx];
        const ingH = C.INGREDIENT_HEIGHT;
        const ingW = 90; // Slightly compact width for clustered items
        const gapX = 10;
        
        const count = ingredients.length;
        const totalGroupWidth = count * ingW + (count - 1) * gapX;
        // Center in lane
        const startX = C.PADDING_LEFT + laneIdx * C.LANE_WIDTH + (C.LANE_WIDTH - totalGroupWidth) / 2;
        
        ingredients.forEach((ingId, idx) => {
            const ingNode = graph.nodes.find(n => n.id === ingId)!;
            const ingX = startX + idx * (ingW + gapX);
            const ingY = clusterY;
            
            nodePosMap.set(ingId, { x: ingX, y: ingY, width: ingW, height: ingH });
            nodes.push({ id: ingId, type: ingNode.type, x: ingX, y: ingY, width: ingW, height: ingH, data: ingNode });
        });
        
        // Advance cursor
        laneYCursors[laneIdx] += ingH + C.GAP_Y * 0.8; // Tighter gap to step
    }

    // Check dependencies for main node
    let minDepY = 0;
    if (node.inputs) {
        node.inputs.forEach(inputId => {
            const inputPos = nodePosMap.get(inputId);
            if (inputPos) {
                minDepY = Math.max(minDepY, inputPos.y + inputPos.height + C.GAP_Y);
            }
        });
    }

    const currentLaneY = laneYCursors[laneIdx];
    const y = Math.max(minDepY, currentLaneY);
    const laneX = C.PADDING_LEFT + laneIdx * C.LANE_WIDTH;
    const x = laneX + (C.LANE_WIDTH - width) / 2;

    nodePosMap.set(node.id, { x, y, width, height });
    laneYCursors[laneIdx] = y + height + C.GAP_Y;

    nodes.push({ id: node.id, type: node.type, x, y, width, height, data: node });
  });

  generateEdges(graph, nodes, nodePosMap, edges, 'vertical');
  
  const layoutHeight = Math.max(...laneYCursors, 600) + C.PADDING_TOP;
  const layoutWidth = C.PADDING_LEFT + graph.lanes.length * C.LANE_WIDTH + C.PADDING_LEFT;
  visualLanes.forEach(l => l.height = layoutHeight);

  return { nodes, edges, lanes: visualLanes, width: layoutWidth, height: layoutHeight };
};

// --- HELPERS ---

function analyzeGraph(graph: RecipeGraph) {
  const nodeMap = new Map<string, RecipeNode>();
  const consumerMap = new Map<string, string[]>();

  graph.nodes.forEach(node => {
      nodeMap.set(node.id, node);
      if (node.inputs) {
          node.inputs.forEach(inputId => {
              if (!consumerMap.has(inputId)) consumerMap.set(inputId, []);
              consumerMap.get(inputId)!.push(node.id);
          });
      }
  });

  const ranks = new Map<string, number>();
  
  const getActionRank = (id: string, visited = new Set<string>()): number => {
    if (visited.has(id)) return 0;
    if (ranks.has(id)) return ranks.get(id)!;
    visited.add(id);
    const node = nodeMap.get(id);
    if (node?.type === 'ingredient') return 0;

    let maxInputRank = -1;
    if (node?.inputs) {
        for (const inputId of node.inputs) {
            const inputNode = nodeMap.get(inputId);
            if (inputNode?.type === 'action') {
                const r = getActionRank(inputId, new Set(visited));
                if (r > maxInputRank) maxInputRank = r;
            }
        }
    }
    const rank = maxInputRank + 1;
    ranks.set(id, rank);
    return rank;
  };

  graph.nodes.filter(n => n.type === 'action').forEach(n => getActionRank(n.id));

  graph.nodes.filter(n => n.type === 'ingredient').forEach(ing => {
      const consumers = consumerMap.get(ing.id) || [];
      if (consumers.length === 0) {
          ranks.set(ing.id, 0);
      } else {
          let minConsumerRank = Infinity;
          let hasRankedConsumer = false;
          consumers.forEach(cId => {
              const r = ranks.get(cId);
              if (r !== undefined) {
                 if (r < minConsumerRank) minConsumerRank = r;
                 hasRankedConsumer = true;
              }
          });
          ranks.set(ing.id, hasRankedConsumer ? Math.max(0, minConsumerRank) : 0); 
      }
  });

  return { nodeMap, ranks, consumerMap };
}

function generateEdges(graph: RecipeGraph, nodes: VisualNode[], nodePosMap: Map<string, any>, edges: VisualEdge[], direction: 'vertical' | 'horizontal') {
    graph.nodes.forEach(node => {
        if (node.inputs) {
          node.inputs.forEach(inputId => {
            const source = nodePosMap.get(inputId);
            const target = nodePosMap.get(node.id);
            
            if (source && target) {
              let path = '';
              if (direction === 'vertical') {
                  const startX = source.x + source.width / 2;
                  const startY = source.y + source.height;
                  const endX = target.x + target.width / 2;
                  const endY = target.y;
                  const midY = (startY + endY) / 2;
                  path = `M ${startX} ${startY} C ${startX} ${midY}, ${endX} ${midY}, ${endX} ${endY}`;
              } else {
                  // Horizontal
                  const startX = source.x + source.width;
                  const startY = source.y + source.height / 2;
                  const endX = target.x;
                  const endY = target.y + target.height / 2;
                  const midX = (startX + endX) / 2;
                  path = `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
              }
              
              edges.push({
                id: `${inputId}->${node.id}`,
                sourceId: inputId,
                targetId: node.id,
                path
              });
            }
          });
        }
    });
}

function emptyGraph(): LayoutGraph {
    return { nodes: [], edges: [], lanes: [], width: 800, height: 600 };
}